import React, { useEffect, useMemo, useRef, useState } from "react";
import mammoth from "mammoth";
import TurndownService from "turndown";
import { hasSupabaseConfig, supabase } from "../../lib/supabaseClient";
import { BLOG_POSTS, mapDbPostToUi } from "../blog/blogData";

const BLOG_COVERS_BUCKET = "blog-covers";

const turndown = new TurndownService({ headingStyle: "atx", bulletListMarker: "-" });
turndown.addRule("removeEmpty", {
  filter: (node) => node.nodeType === 1 && node.textContent?.trim() === "",
  replacement: () => "",
});

function slugify(text = "") {
  return text
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9\s-]/g, "")
    .trim()
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-");
}

function normalizeTextBlocks(text = "") {
  return String(text)
    .replace(/\r\n/g, "\n")
    .replace(/\r/g, "\n")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
}

function stripRtf(rtf = "") {
  let text = String(rtf);

  // Remove noisy metadata groups first (font tables, color tables, generator, etc.)
  text = text
    .replace(/\{\\fonttbl[\s\S]*?\}/g, "")
    .replace(/\{\\colortbl[\s\S]*?\}/g, "")
    .replace(/\{\\stylesheet[\s\S]*?\}/g, "")
    .replace(/\{\\\*\\generator[\s\S]*?\}/g, "")
    .replace(/\{\\\*\\[^{}]+\}/g, "");

  // Convert some formatting markers to markdown-like output
  text = text
    .replace(/\\par[d]?/g, "\n\n")
    .replace(/\\line/g, "\n")
    .replace(/\\tab/g, " ")
    .replace(/\\b\s+([^\\{}]+?)\\b0/g, "**$1**")
    .replace(/\\i\s+([^\\{}]+?)\\i0/g, "*$1*");

  // Decode escaped hex chars like \'e1
  text = text.replace(/\\'([0-9a-fA-F]{2})/g, (_m, h) => {
    const code = parseInt(h, 16);
    return Number.isNaN(code) ? "" : String.fromCharCode(code);
  });

  // Drop remaining control words and braces
  text = text
    .replace(/\\[a-z]+-?\d* ?/gi, "")
    .replace(/[{}]/g, "")
    .replace(/[\u0000-\u001F]/g, " ")
    .replace(/\s{2,}/g, " ");

  const cleaned = normalizeTextBlocks(text)
    .split("\n")
    .map((line) => line.trim())
    .filter((line) => {
      const l = line.toLowerCase();
      if (!l) return false;
      // Skip leftover font declarations/noise lines
      if (l.includes("timesnewroman") || l.includes("arial") || l.includes("calibri")) return false;
      if (/^[;:,.'"\\/|`~\-_=+*]+$/.test(l)) return false;
      return true;
    })
    .join("\n");

  return normalizeTextBlocks(cleaned);
}

function inferTitleFromContent(text = "") {
  const bad = /(timesnewroman|arial|calibri|boldmt|italicmt|tightenfactor|^\\+$)/i;
  const lines = text
    .split(/\n+/)
    .map((l) => l.trim())
    .filter((l) => l && !bad.test(l));

  let first = lines.find((l) => l.length > 3) || "";

  // Clean section headings accidentally appended to title
  first = first
    .replace(/\b(introducci[oó]n|resumen|puntos clave|desarrollo|conclusi[oó]n|fuentes)\b.*$/i, "")
    .replace(/[|•]+/g, " ")
    .replace(/\s{2,}/g, " ")
    .trim();

  // If title is too long, cut at sentence boundary
  if (first.length > 80) {
    const cut = first.search(/[.!?]/);
    if (cut > 15) first = first.slice(0, cut + 1);
  }

  return first.replace(/^#+\s*/, "").slice(0, 95).trim();
}

function inferTitleFromFilename(name = "") {
  return String(name)
    .replace(/\.[a-z0-9]+$/i, "")
    .replace(/[_-]+/g, " ")
    .replace(/\s{2,}/g, " ")
    .trim()
    .slice(0, 120);
}

function stripSitePrefix(title = "") {
  return String(title || "")
    .replace(/^prel\s*blog\s*[–—-]\s*/i, "")
    .replace(/^puerto\s*rico\s*esports\s*league\s*[–—-]\s*/i, "")
    .trim();
}

function ensureUniqueSlug(baseSlug = "", existingSlugs = [], keepSlug = "") {
  const cleaned = slugify(baseSlug || "");
  if (!cleaned) return "";
  const used = new Set((existingSlugs || []).filter(Boolean));
  if (keepSlug) used.delete(keepSlug);
  if (!used.has(cleaned)) return cleaned;

  let i = 2;
  while (used.has(`${cleaned}-${i}`)) i += 1;
  return `${cleaned}-${i}`;
}

function htmlToMarkdown(html = "") {
  const raw = String(html || "");
  const parser = new DOMParser();
  const doc = parser.parseFromString(raw, "text/html");

  doc.querySelectorAll("script,style,noscript,iframe,svg,canvas,meta,link,head,title").forEach((n) => n.remove());

  const heroTitleRaw =
    doc.querySelector(".hero h1")?.textContent?.trim() ||
    doc.querySelector("article h1")?.textContent?.trim() ||
    doc.querySelector("main h1")?.textContent?.trim() ||
    doc.querySelector("h1")?.textContent?.trim() ||
    doc.title ||
    "";
  const heroTitle = stripSitePrefix(heroTitleRaw);
  const heroCategory = doc.querySelector(".category-label")?.textContent?.trim() || "";

  // Preserve premium blocks from legacy HTML template
  doc.querySelectorAll(".cycle-grid").forEach((grid) => {
    const rows = Array.from(grid.querySelectorAll(".cycle-step")).map((step) => {
      const num = step.querySelector(".step-num")?.textContent?.trim() || "";
      const title = step.querySelector(".step-title")?.textContent?.trim() || "";
      const body = step.querySelector(".step-body")?.textContent?.trim() || "";
      return `${num}|${title}|${body}`;
    }).filter(Boolean);

    const token = doc.createElement("p");
    token.textContent = `:::cycle\n${rows.join("\n")}\n:::`;
    grid.replaceWith(token);
  });

  doc.querySelectorAll(".tips-list").forEach((list) => {
    const rows = Array.from(list.querySelectorAll("li")).map((li) => {
      const icon = li.querySelector(".icon")?.textContent?.trim() || "•";
      const title = li.querySelector("strong")?.textContent?.trim() || "";
      const body = li.querySelector("p")?.textContent?.trim() || "";
      return `${icon}|${title}|${body}`;
    }).filter(Boolean);

    const token = doc.createElement("p");
    token.textContent = `:::tips\n${rows.join("\n")}\n:::`;
    list.replaceWith(token);
  });

  doc.querySelectorAll(".stat-block, .stats-block").forEach((block) => {
    const title =
      block.querySelector(".stat-title")?.textContent?.trim() ||
      block.querySelector("h3")?.textContent?.trim() ||
      "Datos clave";

    // Legacy template (.stat-item .num/.desc)
    let rows = Array.from(block.querySelectorAll(".stat-item"))
      .map((item) => {
        const num = item.querySelector(".num")?.textContent?.trim() || "";
        const desc = item.querySelector(".desc")?.textContent?.trim() || "";
        return `${num}|${desc}`;
      })
      .filter(Boolean);

    // New SEO template (.stats-block .stat-row with .ppm/.stat-label)
    if (!rows.length) {
      rows = Array.from(block.querySelectorAll(".stat-row"))
        .map((item) => {
          const num = item.querySelector(".ppm")?.textContent?.trim() || "";
          const desc = item.querySelector(".stat-label")?.textContent?.trim() || "";
          return `${num}|${desc}`;
        })
        .filter(Boolean);
    }

    const tableLines = rows
      .map((row) => {
        const [num = "", desc = ""] = row.split("|").map((x) => x.trim());
        return `| ${num} | ${desc} |`;
      })
      .join("\n");

    const token = doc.createElement("p");
    token.textContent = `${title}\n\n| Nivel CO₂ | Impacto cognitivo |\n|---|---|\n${tableLines}`;
    block.replaceWith(token);
  });

  doc.querySelectorAll(".callout").forEach((node) => {
    const text = node.textContent?.trim() || "";
    const token = doc.createElement("p");
    token.textContent = `> ${text}`;
    node.replaceWith(token);
  });

  const articleRoot = doc.querySelector("article") || doc.querySelector("main") || doc.body;
  const articleHtml = articleRoot?.innerHTML || raw;

  return {
    markdown: turndown.turndown(articleHtml),
    meta: {
      title: heroTitle,
      category: heroCategory,
    },
  };
}

function cleanupImportedContent(text = "") {
  const tokenNormalized = String(text)
    // Normalize premium block tokens so they always sit on their own blocks
    .replace(/:::\s*(callout|cta|cycle|tips|stats)\s*/gi, "\n\n:::$1\n")
    .replace(/\n?:::\s*\n?/g, "\n:::\n");

  const rough = tokenNormalized
    .replace(/\\/g, "")
    .replace(/\btightenfactor\d*\b/gi, "")
    .replace(/\b(?:Arial|TimesNewRoman|Calibri)[A-Za-z0-9-]*\b/gi, "")
    .replace(/<!doctype[^>]*>/gi, "")
    .replace(/<\/?(?:html|head|body)[^>]*>/gi, "")
    // Ensure markdown headings start on their own line
    .replace(/\s+(#{1,6}\s+)/g, "\n\n$1")
    // Normalize underscore italics from DOCX converters -> asterisk italics
    .replace(/(^|\s)_([^_\n]+)_($|\s)/g, "$1*$2*$3")
    .replace(/\n{3,}/g, "\n\n")
    .replace(/[ \t]{2,}/g, " ");

  const cleaned = rough
    .split("\n")
    .map((line) => line.trim())
    .filter((line) => {
      if (!line) return false;
      const l = line.toLowerCase();

      // Drop obvious CSS/JS/code noise from pasted HTML exports
      if (/^[.#@][a-z0-9_-]+\s*\{/.test(l)) return false;
      if (/^(var|const|let)\s+[a-z0-9_$]+\s*=/.test(l)) return false;
      if (/^(function\s*\(|export\s+default|import\s+)/.test(l)) return false;
      if (/\{\s*$|^\}\s*;?$/.test(l)) return false;
      if (l.includes("font-family") || l.includes("background:") || l.includes("display:") || l.includes("position:")) return false;
      if (l.startsWith("--") && l.includes(":")) return false;

      return true;
    })
    .join("\n");

  return normalizeTextBlocks(cleaned);
}

function seoTemplate(title = "") {
  const safeTitle = title || "Título del artículo";
  return `# ${safeTitle}

## Resumen
Escribe aquí un resumen claro del tema y su impacto para la comunidad.

## Puntos clave
- Punto 1
- Punto 2
- Punto 3

## Desarrollo
Explica el contexto, los datos relevantes y recomendaciones prácticas.

## Conclusión
Cierre con CTA claro (ej. regístrate / comenta / comparte).

## Fuentes
- Fuente principal
`;
}

function premiumTemplate(title = "") {
  const safeTitle = title || "Título del artículo";
  return `# ${safeTitle}

## Contexto
Explica en 1-2 párrafos por qué este tema importa para la comunidad PREL.

:::callout
Idea clave que quieres resaltar en grande.
:::

## Datos clave
| Métrica | Valor | Nota |
|---|---|---|
| Indicador 1 | Valor | Contexto |
| Indicador 2 | Valor | Contexto |

## Recomendaciones prácticas
- Acción 1
- Acción 2
- Acción 3

## Cierre
Resumen final + llamado a la acción.

:::cta
¿Listo para competir? Regístrate y súmate a la próxima activación PREL.
:::

## Fuentes
- Fuente principal
`;
}

function renderInline(text = "") {
  const parts = text
    .split(/(\[[^\]]+\]\([^\)]+\)|\*\*[^*]+\*\*|\*[^*]+\*|_[^_]+_)/g)
    .filter(Boolean);

  return parts.map((part, idx) => {
    const linkMatch = part.match(/^\[([^\]]+)\]\(([^\)]+)\)$/);
    if (linkMatch) {
      const [, label, href] = linkMatch;
      return (
        <a key={idx} href={href} target="_blank" rel="noopener noreferrer" className="text-cyan-300 hover:underline">
          {label}
        </a>
      );
    }
    if (part.startsWith("**") && part.endsWith("**")) return <strong key={idx}>{part.slice(2, -2)}</strong>;
    if (part.startsWith("*") && part.endsWith("*")) return <em key={idx}>{part.slice(1, -1)}</em>;
    if (part.startsWith("_") && part.endsWith("_")) return <em key={idx}>{part.slice(1, -1)}</em>;
    return <React.Fragment key={idx}>{part}</React.Fragment>;
  });
}

function renderMarkdown(content = "") {
  const normalized = String(content)
    .replace(/\\n/g, "\n")
    .replace(/\\t/g, "\t")
    .replace(/\r\n/g, "\n")
    .replace(/\r/g, "\n")
    .replace(/:::\s*(callout|cta|cycle|tips|stats)\s*/gi, "\n\n:::$1\n")
    .replace(/\n?:::\s*\n?/g, "\n:::\n")
    // Recover flattened markdown tables (common after HTML import via turndown)
    .replace(/\s+(\|\s*[^|\n]+\s*\|\s*[-:|\s]+\|)/g, "\n\n$1")
    .replace(/(\|\s*[-:|\s]+\|)\s+(\|\s*[^|\n]+\s*\|\s*[^|\n]+\s*\|)/g, "$1\n$2")
    .replace(/(\|\s*[^|\n]+\s*\|\s*[^|\n]+\s*\|)\s+(?=\|\s*[^|\n]+\s*\|\s*[^|\n]+\s*\|)/g, "$1\n")
    .replace(/\s+(#{1,6}\s+)/g, "\n\n$1")
    .replace(/(^|\n)\s*(#{1,6}\s+[^\n]+)/g, "\n\n$2\n\n")
    .replace(/\n{3,}/g, "\n\n");

  const blocks = normalized.split(/\n\s*\n+/).map((b) => b.trim()).filter(Boolean);

  return blocks.map((block, idx) => {
    const lines = block.split("\n").map((l) => l.trim()).filter(Boolean);
    if (!lines.length) return null;
    const first = lines[0];

    if (/^:::\s*cycle\b/i.test(first)) {
      const rows = lines.slice(1).filter((l) => l && l !== ":::");
      return (
        <div key={idx} className="rounded border border-white/10 overflow-hidden">
          {rows.map((r, i) => {
            const [num = "", title = "", body = ""] = r.split("|").map((x) => x.trim());
            return (
              <div key={i} className="px-3 py-2 border-b border-white/10 last:border-b-0">
                <strong className="text-white">{num} {title}</strong>
                <p className="text-slate-300 text-xs mt-1">{body}</p>
              </div>
            );
          })}
        </div>
      );
    }

    if (/^:::\s*tips\b/i.test(first)) {
      const rows = lines.slice(1).filter((l) => l && l !== ":::");
      return (
        <div key={idx} className="space-y-2">
          {rows.map((r, i) => {
            const [icon = "•", title = "", body = ""] = r.split("|").map((x) => x.trim());
            return (
              <div key={i} className="rounded border border-white/10 px-3 py-2">
                <strong className="text-white">{icon} {title}</strong>
                <p className="text-slate-300 text-xs mt-1">{body}</p>
              </div>
            );
          })}
        </div>
      );
    }

    if (/^:::\s*stats\b/i.test(first)) {
      const rows = lines.slice(1).filter((l) => l && l !== ":::");
      const title = rows[0] || "Datos clave";
      const items = rows.slice(1);
      return (
        <div key={idx} className="rounded border border-white/10 px-3 py-3">
          <p className="text-xs uppercase tracking-wide text-red-300 mb-2">{title}</p>
          <div className="space-y-1">
            {items.map((r, i) => {
              const [num = "", desc = ""] = r.split("|").map((x) => x.trim());
              return <p key={i} className="text-xs text-slate-300"><strong className="text-white">{num}</strong> — {desc}</p>;
            })}
          </div>
        </div>
      );
    }

    if (/^:::\s*callout\b/i.test(first)) {
      const body = lines.filter((l, i) => i !== 0 && l !== ":::").join(" ").trim();
      return <div key={idx} className="rounded border border-white/10 px-3 py-2 text-sm font-semibold text-white">{body}</div>;
    }

    if (/^###\s+/.test(first)) {
      const rest = lines.slice(1).join("\n").trim();
      return (
        <React.Fragment key={idx}>
          <h3 className="text-lg font-semibold">{renderInline(first.replace(/^###\s+/, ""))}</h3>
          {rest ? renderMarkdown(rest) : null}
        </React.Fragment>
      );
    }
    if (/^##\s+/.test(first)) {
      const rest = lines.slice(1).join("\n").trim();
      return (
        <React.Fragment key={idx}>
          <h2 className="text-xl font-bold">{renderInline(first.replace(/^##\s+/, ""))}</h2>
          {rest ? renderMarkdown(rest) : null}
        </React.Fragment>
      );
    }
    if (/^#\s+/.test(first)) {
      const rest = lines.slice(1).join("\n").trim();
      return (
        <React.Fragment key={idx}>
          <h1 className="text-2xl font-bold">{renderInline(first.replace(/^#\s+/, ""))}</h1>
          {rest ? renderMarkdown(rest) : null}
        </React.Fragment>
      );
    }

    const isMarkdownTable =
      lines.length >= 2 &&
      lines[0].includes("|") &&
      /^\|?\s*[:\-\|\s]+\|?\s*$/.test(lines[1]);

    if (isMarkdownTable) {
      const splitRow = (row) =>
        row
          .split("|")
          .map((c) => c.trim())
          .filter((c, i, arr) => !(i === 0 && c === "") && !(i === arr.length - 1 && c === ""));

      const headers = splitRow(lines[0]);
      const bodyRows = lines.slice(2).map(splitRow).filter((r) => r.length);

      return (
        <div key={idx} className="overflow-x-auto rounded border border-white/10">
          <table className="w-full text-xs">
            <thead className="bg-white/10 text-white">
              <tr>{headers.map((h, hIdx) => <th key={hIdx} className="text-left px-2 py-1 border-b border-white/20">{renderInline(h)}</th>)}</tr>
            </thead>
            <tbody>
              {bodyRows.map((row, rIdx) => (
                <tr key={rIdx} className="border-b border-white/10 last:border-b-0">
                  {row.map((cell, cIdx) => <td key={cIdx} className="px-2 py-1 text-slate-200">{renderInline(cell)}</td>)}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    }

    const isBullet = lines.every((l) => /^[-*]\s+/.test(l));
    if (isBullet) {
      return <ul key={idx} className="list-disc pl-5 space-y-1">{lines.map((l, i) => <li key={i}>{renderInline(l.replace(/^[-*]\s+/, ""))}</li>)}</ul>;
    }

    const isNumbered = lines.every((l) => /^\d+\.\s+/.test(l));
    if (isNumbered) {
      return <ol key={idx} className="list-decimal pl-5 space-y-1">{lines.map((l, i) => <li key={i}>{renderInline(l.replace(/^\d+\.\s+/, ""))}</li>)}</ol>;
    }

    return <p key={idx}>{renderInline(lines.join(" "))}</p>;
  });
}

export default function AdminBlogPage() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [msg, setMsg] = useState("");
  const [form, setForm] = useState({
    title: "",
    slug: "",
    excerpt: "",
    content: "",
    cover: "/friday.png",
    category: "Noticias",
    status: "published",
  });
  const [editingSlug, setEditingSlug] = useState("");
  const [showPreview, setShowPreview] = useState(true);

  const canWrite = hasSupabaseConfig && supabase;
  const importRef = useRef(null);
  const coverRef = useRef(null);

  const seo = useMemo(() => {
    const title = form.title?.trim() || "";
    const excerpt = form.excerpt?.trim() || "";
    return {
      titleLen: title.length,
      excerptLen: excerpt.length,
      titleOk: title.length >= 45 && title.length <= 65,
      excerptOk: excerpt.length >= 140 && excerpt.length <= 165,
      readingMin: Math.max(1, Math.round((form.content || "").split(/\s+/).filter(Boolean).length / 220)),
    };
  }, [form.title, form.excerpt, form.content]);

  async function loadPosts() {
    setLoading(true);
    setMsg("");
    try {
      if (!canWrite) {
        setPosts(BLOG_POSTS);
        return;
      }
      const { data, error } = await supabase
        .from("blog_posts")
        .select("id,slug,title,excerpt,content_md,cover_image_url,category,author_name,published_at,created_at,status")
        .order("created_at", { ascending: false })
        .limit(200);
      if (error) throw error;
      setPosts((data || []).map((d) => ({ ...mapDbPostToUi(d), status: d.status || "draft" })));
    } catch (e) {
      setMsg(`Error cargando posts: ${e.message}`);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadPosts();
  }, []);

  function onChange(k, v) {
    setForm((p) => ({ ...p, [k]: v }));
  }

  function onTitleChange(value) {
    setForm((p) => ({
      ...p,
      title: value,
      slug: p.slug ? p.slug : slugify(value),
      excerpt: p.excerpt || `Guía práctica sobre ${value || "este tema"} para la comunidad de Puerto Rico Esports League.`,
    }));
  }

  function applySeoTemplate() {
    setForm((p) => ({
      ...p,
      slug: p.slug || slugify(p.title),
      excerpt:
        p.excerpt ||
        "Guía práctica basada en datos para mejorar rendimiento competitivo en esports de Puerto Rico.",
      content: p.content || seoTemplate(p.title),
    }));
  }

  function applyPremiumTemplate() {
    setForm((p) => ({
      ...p,
      slug: p.slug || slugify(p.title),
      excerpt:
        p.excerpt ||
        "Análisis claro y accionable para la comunidad competitiva de Puerto Rico Esports League.",
      content: p.content || premiumTemplate(p.title),
    }));
  }

  function resetForm() {
    setForm({ title: "", slug: "", excerpt: "", content: "", cover: "/friday.png", category: "Noticias", status: "published" });
    setEditingSlug("");
  }

  function editPost(post) {
    setEditingSlug(post.slug || "");
    setForm({
      title: post.title || "",
      slug: post.slug || "",
      excerpt: post.excerpt || "",
      content: post.contentMd || (Array.isArray(post.content) ? post.content.join("\n\n") : ""),
      cover: post.cover || "/friday.png",
      category: post.category || "Noticias",
      status: post.status || "published",
    });
  }

  async function onImportFile(e) {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      const lower = file.name.toLowerCase();
      let imported = "";
      let htmlTitle = "";
      let htmlCategory = "";

      if (lower.endsWith(".docx")) {
        const buffer = await file.arrayBuffer();
        const { value: html } = await mammoth.convertToHtml({ arrayBuffer: buffer });
        const parsed = htmlToMarkdown(html || "");
        imported = parsed.markdown;
        htmlTitle = parsed.meta?.title || "";
        htmlCategory = parsed.meta?.category || "";
      } else {
        const raw = await file.text();
        if (lower.endsWith(".html") || lower.endsWith(".htm")) {
          const parsed = htmlToMarkdown(raw || "");
          imported = parsed.markdown;
          htmlTitle = parsed.meta?.title || "";
          htmlCategory = parsed.meta?.category || "";
        } else if (lower.endsWith(".rtf")) {
          imported = stripRtf(raw);
        } else {
          imported = normalizeTextBlocks(raw);
        }
      }

      const content = cleanupImportedContent(imported);
      const titleFromContent = inferTitleFromContent(content);
      const titleFromFile = inferTitleFromFilename(file.name);
      const guessedTitle = htmlTitle || titleFromContent || titleFromFile || "Nuevo artículo";

      setForm((p) => {
        const nextTitle = guessedTitle;
        const defaultExcerpt = "Artículo sobre Nuevo artículo para la comunidad de Puerto Rico Esports League.";
        const autoSlugCurrent = slugify(p.title || "");
        const shouldReplaceSlug = !p.slug || p.slug === "nuevo-articulo" || p.slug === autoSlugCurrent;
        const nextSlugBase = shouldReplaceSlug ? slugify(nextTitle) : p.slug;
        const nextSlug = ensureUniqueSlug(nextSlugBase, posts.map((x) => x.slug), editingSlug || "");

        return {
          ...p,
          title: nextTitle,
          slug: nextSlug,
          excerpt:
            p.excerpt && p.excerpt !== defaultExcerpt
              ? p.excerpt
              : `Artículo sobre ${nextTitle} para la comunidad de Puerto Rico Esports League.`,
          category: htmlCategory || p.category,
          content: content || p.content,
        };
      });

      setMsg(`✅ Archivo importado: ${file.name} · título detectado: ${guessedTitle}`);
    } catch {
      setMsg("❌ No se pudo leer el archivo. Usa .docx (recomendado), .txt, .md, .rtf o .html.");
    } finally {
      if (importRef.current) importRef.current.value = "";
    }
  }

  async function onCoverUpload(e) {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!canWrite) {
      setMsg("Configura Supabase para subir portada.");
      return;
    }

    try {
      const safe = file.name.replace(/\s+/g, "-").toLowerCase();
      const path = `covers/${Date.now()}-${safe}`;
      const { error } = await supabase.storage.from(BLOG_COVERS_BUCKET).upload(path, file, {
        upsert: false,
        contentType: file.type || undefined,
      });
      if (error) throw error;
      const { data } = supabase.storage.from(BLOG_COVERS_BUCKET).getPublicUrl(path);
      if (!data?.publicUrl) throw new Error("No se pudo obtener URL pública de portada.");

      setForm((p) => ({ ...p, cover: data.publicUrl }));
      setMsg("✅ Portada subida y asignada al post.");
    } catch (e2) {
      setMsg(`❌ Error subiendo portada: ${e2.message}`);
    } finally {
      if (coverRef.current) coverRef.current.value = "";
    }
  }

  async function deletePostBySlug(slug) {
    if (!canWrite || !slug) return;
    const ok = window.confirm(`¿Borrar este post?\n\n/${slug}`);
    if (!ok) return;

    try {
      const { error } = await supabase.from("blog_posts").delete().eq("slug", slug);
      if (error) throw error;
      setMsg("✅ Post borrado.");
      if (editingSlug === slug) resetForm();
      await loadPosts();
    } catch (e) {
      setMsg(`❌ No se pudo borrar: ${e.message}`);
    }
  }

  async function onSubmit(e) {
    e.preventDefault();
    setMsg("");
    if (!canWrite) {
      setMsg("Configura Supabase para crear posts reales.");
      return;
    }

    const baseSlug = slugify(form.slug || form.title);
    if (!baseSlug) {
      setMsg("Añade título o slug válido.");
      return;
    }

    const cleanSlug = ensureUniqueSlug(baseSlug, posts.map((p) => p.slug), editingSlug || "");

    try {
      const payload = {
        slug: cleanSlug,
        title: form.title,
        excerpt: form.excerpt,
        content_md: form.content,
        cover_image_url: form.cover,
        category: form.category,
        status: form.status,
        author_name: "PR Esports League",
        published_at: form.status === "published" ? new Date().toISOString() : null,
      };
      const { error } = await supabase.from("blog_posts").upsert(payload, { onConflict: "slug" });
      if (error) throw error;
      const slugNote = cleanSlug !== baseSlug ? ` (slug ajustado a /${cleanSlug} para evitar conflicto)` : "";
      setMsg((editingSlug ? "✅ Post actualizado/publicado." : "✅ Post guardado/publicado.") + slugNote);
      resetForm();
      await loadPosts();
    } catch (e) {
      const raw = e?.message || "Error inesperado";
      if (raw.toLowerCase().includes("row-level security") || raw.includes("42501")) {
        setMsg("❌ Tu usuario no tiene permisos para insertar en blog_posts (RLS). Hay que crear/ajustar policy de INSERT para admin_users.");
      } else {
        setMsg(`❌ ${raw}`);
      }
    }
  }

  return (
    <div className="min-h-screen bg-[#06070A] text-white p-6 md:p-10">
      <div className="max-w-7xl mx-auto grid lg:grid-cols-5 gap-6">
        <div className="lg:col-span-2 rounded-2xl border border-white/10 bg-[#111722] p-5">
          <div className="flex gap-3 text-sm">
            <a href="/" className="text-cyan-300 hover:underline">← Home</a>
            <a href="/admin/dashboard" className="text-cyan-300 hover:underline">Dashboard</a>
            <a href="/admin/media" className="text-cyan-300 hover:underline">Directorio PREL</a>
          </div>
          <h1 className="text-2xl font-bold mt-3">Admin Blog</h1>
          <p className="text-slate-300 text-sm mt-1">Publicación rápida con SEO básico, slug automático e importación de .docx/.txt/.md/.rtf/.html (Word/Pages recomendado exportar en .docx).</p>
          {editingSlug ? <p className="text-xs text-amber-200 mt-2">Editando: <code>/{editingSlug}</code></p> : null}

          <div className="mt-3 flex flex-wrap gap-2">
            <button type="button" onClick={applySeoTemplate} className="text-xs px-3 py-1.5 rounded border border-cyan-300/30 text-cyan-200 hover:bg-cyan-400/10">
              Cargar plantilla SEO
            </button>
            <button type="button" onClick={applyPremiumTemplate} className="text-xs px-3 py-1.5 rounded border border-red-300/30 text-red-200 hover:bg-red-400/10">
              Cargar plantilla Premium
            </button>
            <button type="button" onClick={() => importRef.current?.click()} className="text-xs px-3 py-1.5 rounded border border-violet-300/30 text-violet-200 hover:bg-violet-400/10">
              Importar archivo
            </button>
            <button type="button" onClick={() => setShowPreview((v) => !v)} className="text-xs px-3 py-1.5 rounded border border-white/20 hover:bg-white/5">
              {showPreview ? "Ocultar visualizador" : "Mostrar visualizador"}
            </button>
            <input
              ref={importRef}
              type="file"
              accept=".docx,.md,.markdown,.txt,.rtf,.html,.htm,text/plain,text/markdown,text/html,application/rtf,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
              className="hidden"
              onChange={onImportFile}
            />
          </div>

          <form className="mt-4 space-y-3" onSubmit={onSubmit}>
            <input placeholder="Título" value={form.title} onChange={(e) => onTitleChange(e.target.value)} className="w-full rounded-lg bg-[#0b1019] border border-white/15 px-3 py-2" required />
            <div className="grid grid-cols-4 gap-2">
              <input placeholder="Slug" value={form.slug} onChange={(e) => onChange("slug", slugify(e.target.value))} className="col-span-3 w-full rounded-lg bg-[#0b1019] border border-white/15 px-3 py-2" required />
              <button type="button" onClick={() => onChange("slug", slugify(form.title))} className="rounded-lg border border-white/20 text-sm">Auto</button>
            </div>
            <input placeholder="Excerpt" value={form.excerpt} onChange={(e) => onChange("excerpt", e.target.value)} className="w-full rounded-lg bg-[#0b1019] border border-white/15 px-3 py-2" />
            <div className="grid grid-cols-6 gap-2">
              <input placeholder="Cover URL o /archivo.png" value={form.cover} onChange={(e) => onChange("cover", e.target.value)} className="col-span-4 w-full rounded-lg bg-[#0b1019] border border-white/15 px-3 py-2" />
              <button type="button" onClick={() => coverRef.current?.click()} className="col-span-2 rounded-lg border border-white/20 text-sm hover:bg-white/5">
                Subir imagen
              </button>
              {editingSlug ? (
                <>
                  <button
                    type="button"
                    onClick={() => deletePostBySlug(editingSlug)}
                    className="col-span-2 rounded-lg border border-rose-300/40 text-rose-200 text-sm hover:bg-rose-500/10"
                  >
                    Borrar post
                  </button>
                  <button type="button" onClick={resetForm} className="col-span-2 rounded-lg border border-white/20 text-sm hover:bg-white/5">Cancelar edición</button>
                </>
              ) : (
                <button type="button" onClick={resetForm} className="col-span-2 rounded-lg border border-white/20 text-sm hover:bg-white/5">Limpiar</button>
              )}
              <input
                ref={coverRef}
                type="file"
                accept="image/png,image/jpeg,image/webp,image/gif"
                className="hidden"
                onChange={onCoverUpload}
              />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <input placeholder="Categoría" value={form.category} onChange={(e) => onChange("category", e.target.value)} className="w-full rounded-lg bg-[#0b1019] border border-white/15 px-3 py-2" />
              <select value={form.status} onChange={(e) => onChange("status", e.target.value)} className="w-full rounded-lg bg-[#0b1019] border border-white/15 px-3 py-2">
                <option value="published">published</option>
                <option value="draft">draft</option>
              </select>
            </div>
            <textarea placeholder="Contenido (markdown/simple text)" value={form.content} onChange={(e) => onChange("content", e.target.value)} rows={10} className="w-full rounded-lg bg-[#0b1019] border border-white/15 px-3 py-2" required />
            {showPreview ? (
              <div className="rounded-lg border border-cyan-300/20 bg-[#0b1019] p-3">
                <p className="text-xs text-cyan-200 mb-2">Visualizador del post (antes de publicar)</p>
                {form.cover ? <img src={form.cover} alt="Preview cover" className="w-full h-36 object-cover rounded border border-white/10 mb-3" /> : null}
                <article className="space-y-3 text-sm text-slate-200 leading-relaxed">
                  {renderMarkdown(form.content || `# ${form.title || "Nuevo artículo"}`)}
                </article>
              </div>
            ) : null}
            <button className="w-full py-2.5 rounded-lg bg-cyan-400 text-black font-semibold">{editingSlug ? "Actualizar / Publicar" : "Guardar / Publicar"}</button>
          </form>
          {msg ? <p className="text-sm mt-3 text-slate-200">{msg}</p> : null}

          <div className="mt-4 text-xs text-slate-300 border border-white/10 rounded-lg p-3 bg-[#0b1019]">
            <p className="font-semibold text-white mb-1">SEO check rápido</p>
            <p>Título: {seo.titleLen} caracteres {seo.titleOk ? "✅" : "⚠️"} (ideal 45-65)</p>
            <p>Descripción: {seo.excerptLen} caracteres {seo.excerptOk ? "✅" : "⚠️"} (ideal 140-165)</p>
            <p>Lectura aprox: {seo.readingMin} min</p>
          </div>
        </div>

        <div className="lg:col-span-3 rounded-2xl border border-white/10 bg-[#111722] p-5">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-semibold">Posts</h2>
            <button onClick={loadPosts} className="text-sm text-cyan-300 hover:underline">Refrescar</button>
          </div>
          <div className="mt-4 space-y-3">
            {loading ? <p className="text-slate-300">Cargando...</p> : posts.map((p) => (
              <article key={p.slug} className="rounded-xl border border-white/10 p-3">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <p className="text-xs text-cyan-300 uppercase tracking-wider">{p.category}</p>
                    <h3 className="font-semibold">{p.title}</h3>
                    <p className="text-slate-300 text-sm mt-1">/{p.slug}</p>
                  </div>
                  <div className="flex gap-2">
                    <button type="button" onClick={() => editPost(p)} className="text-xs px-2 py-1 rounded border border-white/20 hover:bg-white/5">Editar</button>
                    {canWrite ? <button type="button" onClick={() => deletePostBySlug(p.slug)} className="text-xs px-2 py-1 rounded border border-rose-300/40 text-rose-200 hover:bg-rose-500/10">Borrar</button> : null}
                  </div>
                </div>
              </article>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
